import { getDisplayedRows } from "@common/tableSearch";
import { EntityType } from "@common/Common.types";
import { RiskAssessmentSubject, RiskScenarioItem, RiskScenarioRow } from "./RiskAssessmentsOverview.types";
import { RiskAssessmentStatus } from "../RiskAssessmentStatus";
import { RiskTreatmentType } from "../riskTreatmentType";

// The subject's base route: the overview lives at it, and selectable-scenarios / scenarios hang off it. Both the
// entity top bar and the scenario table observe the overview through this helper so react-query sees a single key:
// one request, one cache entry, and a refetch from either side updates both.
export const getRiskAssessmentSubjectUrl = (riskAreaId: string, subject: RiskAssessmentSubject) =>
    subject.kind === "entity" ? `/risk-assessments/${riskAreaId}/${subject.entityType}/${subject.entityId}` : `/risk-assessments/${riskAreaId}/organisation`;

// Saved views and the page-view session key are scoped per area and subject; the organisation has no id of its own,
// so it gets a fixed word that cannot collide with an entity id.
export const getRiskAssessmentSubjectKey = (subject: RiskAssessmentSubject) => (subject.kind === "entity" ? subject.entityId : "organisation");

// The subject a row carrying the two nullable entity columns is about, so any risk table can address it the same
// way. Both columns are null together, and that absence is what says the organisation is the subject.
export const toRiskAssessmentSubject = (entityType: EntityType | null, entityId: string | null): RiskAssessmentSubject =>
    entityType !== null && entityId !== null ? { kind: "entity", entityType, entityId } : { kind: "organisation" };

// Mirrors the operational/governance overview: a level strictly above the area appetite is "over appetite", and
// an appetite of 0 means none is configured.
export const isLevelOverAppetite = (level: number, appetite: number): boolean => appetite > 0 && level > appetite;

// The four predicates below are the frontend twins of the organisation fragments the governance tiles are counted
// with, so a tile's figure equals the rows its deep link lands on. Each one names the backend fragment it mirrors;
// they must move together.

// RiskGovernanceQueries.AssessedOrganisationScenarios + LiveOrganisationPosture: the scenario is on the matrix the
// Assessed tile counts. Scored at all (no current revision means never scored), live, and not avoided — a
// scored-then-avoided scenario drops off the matrix, so it is a gap, not an assessment.
export const isAssessedScenario = (row: RiskScenarioItem): boolean =>
    row.risk != null && row.status === RiskAssessmentStatus.Active && row.treatmentType !== RiskTreatmentType.Avoid;

// RiskAssessmentSqlFragments.OverAppetiteOrganisation: live, not avoided, and scored above the area's appetite.
export const isOverAppetiteScenario = (row: RiskScenarioItem, appetite: number): boolean =>
    isAssessedScenario(row) && row.risk != null && isLevelOverAppetite(row.risk, appetite);

// RiskAssessmentSqlFragments.UntreatedOverAppetiteOrganisation: over appetite carrying no treatment decision.
export const isUntreatedOverAppetiteScenario = (row: RiskScenarioItem, appetite: number): boolean =>
    isOverAppetiteScenario(row, appetite) && row.treatmentType === RiskTreatmentType.None;

// RiskAssessmentSqlFragments.UncoveredOverAppetiteOrganisation: over appetite with no control linked to the risk
// itself. Controls hanging off a mitigation plan deliberately do not count as coverage.
export const isUncoveredOverAppetiteScenario = (row: RiskScenarioItem, appetite: number): boolean =>
    isOverAppetiteScenario(row, appetite) && row.linkedControlIds.length === 0;

// A row can take a fresh bulk mitigation plan only when it is scored (risk present), not retired, and not
// already carrying a treatment. Mirrors the backend's CanProposeMitigationPlanWithoutSuperseding guard, so the
// selectable rows are exactly the ones the bulk-mitigate endpoint will act on — a row the user couldn't see
// the result for is never selectable.
export const isMitigatable = (row: RiskScenarioItem): boolean =>
    row.risk != null && row.status !== RiskAssessmentStatus.Retired && row.treatmentType === RiskTreatmentType.None;

// A retired or unscored assessment has nothing to treat: the backend rejects mitigation/acceptance/avoidance,
// so its treatment chips and warnings render inert rather than opening the treatment dialog.
export const isTreatable = (row: RiskScenarioItem): boolean => row.risk != null && row.status !== RiskAssessmentStatus.Retired;

// The tooltip key explaining why a row can't be bulk-mitigated, or undefined when it can. Mirrors the three
// exclusions in isMitigatable so a greyed checkbox can tell the user which one applies.
export const bulkMitigateDisabledReasonKey = (row: RiskScenarioItem): string | undefined => {
    if (row.status === RiskAssessmentStatus.Retired) return "riskBulkMitigateDisabledRetired";
    if (row.risk == null) return "riskBulkMitigateDisabledUnscored";
    if (row.treatmentType !== RiskTreatmentType.None) return "riskBulkMitigateDisabledHasTreatment";
    return undefined;
};

interface RowClickOptions {
    isSelectionMode: boolean;
    isSelectable: boolean;
    isRetired: boolean;
    onToggle: () => void;
    onAssess: () => void;
}

// In selection mode only the selectable rows respond, toggling their checkbox; otherwise a row opens its
// assessment unless it is retired. A row with no handler renders non-clickable.
export const getRowClickHandler = ({ isSelectionMode, isSelectable, isRetired, onToggle, onAssess }: RowClickOptions): (() => void) | undefined => {
    if (isSelectionMode) return isSelectable ? onToggle : undefined;
    return isRetired ? undefined : onAssess;
};

type SortOrder = "asc" | "desc";
type ScenarioComparator = (a: RiskScenarioRow, b: RiskScenarioRow, order: SortOrder) => number;

// Exposure and confidence columns render a formatted string but must sort on their raw number, with unset values
// sinking to the bottom in either direction since a blank carries no amount or judgement.
const numericSortWithBlanksLast =
    (selector: (row: RiskScenarioRow) => number | null): ScenarioComparator =>
    (a, b, order) => {
        const left = selector(a);
        const right = selector(b);
        if (left == null && right == null) return 0;
        if (left == null) return 1;
        if (right == null) return -1;
        const diff = left - right;
        return order === "asc" ? diff : -diff;
    };

// The column sort overrides, keyed by the table header `property`. Shared by the table headers in the component
// and by getDisplayedScenarioRows below, so the export orders rows exactly as the table does.
export const scenarioSortOverrides: Record<string, ScenarioComparator> = {
    // Unassessed scenarios (no score yet) float to the top of the default view so the coverage gap is visible;
    // the scored ones follow, highest risk first. Flips symmetrically when toggled ascending.
    riskLabel: (a, b, order) => {
        if (a.risk == null && b.risk == null) return 0;
        if (a.risk == null) return order === "desc" ? -1 : 1;
        if (b.risk == null) return order === "desc" ? 1 : -1;
        const diff = b.risk - a.risk;
        return order === "desc" ? diff : -diff;
    },
    assessmentDateLabel: (a, b, order) => {
        const left = a.assessedAt ?? "";
        const right = b.assessedAt ?? "";
        return order === "asc" ? left.localeCompare(right) : right.localeCompare(left);
    },
    // No backing row field (the data is the linkedControlIds array), so sort by its length. Rows with no linked
    // controls float to the top in either direction, so the coverage gap stays visible.
    linkedControlCount: (a, b, order) => {
        const aCount = a.linkedControlIds.length;
        const bCount = b.linkedControlIds.length;
        if (aCount === 0 && bCount === 0) return 0;
        if (aCount === 0) return -1;
        if (bCount === 0) return 1;
        const diff = aCount - bCount;
        return order === "asc" ? diff : -diff;
    },
    exposureValue: numericSortWithBlanksLast((row) => row.exposureValue),
    planEstimatedCost: numericSortWithBlanksLast((row) => row.planEstimatedCost),
    planExpectedExposureValue: numericSortWithBlanksLast((row) => row.planExpectedExposureValue),
    confidence: numericSortWithBlanksLast((row) => row.confidence),
};

// Kept out of the search box, shared by the table and getDisplayedScenarioRows below. The note's HTML would match its
// markup ("strong", "li"), so search reaches the note through notePlainText instead.
export const scenarioSearchExcludedKeys: Array<keyof RiskScenarioRow> = ["note"];

// The rows exactly as the table shows them, with this page's comparator overrides applied. Exported for unit testing.
export const getDisplayedScenarioRows = (rows: Array<RiskScenarioRow>, searchString: string, sortBy: string, sortOrder: SortOrder): Array<RiskScenarioRow> =>
    getDisplayedRows(rows, searchString, sortBy, sortOrder, scenarioSortOverrides, scenarioSearchExcludedKeys);
